/**
 * GroupChallengeHistorySection
 * Renders challenge history below the group leaderboard: picker + MatchResultCard per challenge.
 * Uses same cards as 1v1 history; default opponent = rank-#1 other member; Show more for pagination.
 */

import { useCallback, useMemo, useState, useEffect } from 'react'
import { View, Text, Pressable, Platform } from 'react-native'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations'
import type { ChallengeWithStats } from '../../../types/challenges'
import type { ChallengeWithParticipantAvatars } from '../../../types/challenges'
import {
  type GroupLeaderboardEntry,
  getAcceptedParticipantIds,
} from '../../../src/friends/helpers/groupLeaderboard.helper'
import { groupChallengeToPickHistoryEntry } from '../../../src/friends/helpers/groupChallengeToPickHistoryEntry.helper'
import { ChallengeParticipantSwitcher } from '../../challenges/ChallengeParticipantSwitcher'
import { MatchResultCard } from '../friendStats/MatchResultCard'
import { getGroupLeaderboardStyles } from '../../../appStyles/_groupLeaderboard.styles'
import { useTheme } from '../../../src/theme/useTheme'
import {
  GROUP_CHALLENGE_HISTORY_TITLE,
  GROUP_CHALLENGE_HISTORY_INITIAL_COUNT,
  GROUP_CHALLENGE_HISTORY_PAGE_SIZE,
  GROUP_CHALLENGE_HISTORY_EMPTY_MESSAGE,
  GROUP_CHALLENGE_HISTORY_SHOW_MORE,
} from '../../../src/friends/constants/groups.constants'
import type { Participant } from '../../../src/challenges/types/challengeCard.types'

export interface GroupChallengeHistorySectionProps {
  challenges: ChallengeWithStats[]
  currentUserId: string
  leaderboardEntries: GroupLeaderboardEntry[]
  memberNames: Record<string, string>
  userAvatarId?: string | null
  testID?: string
}

/** Accepted participants only (excl. current user). Order: leaderboard order, then by accepted id. */
export function buildAcceptedOtherParticipants(
  acceptedSet: Set<string>,
  currentUserId: string,
  leaderboardEntries: GroupLeaderboardEntry[],
  memberNames: Record<string, string>
): Participant[] {
  const otherIds = [...acceptedSet].filter((id) => id !== currentUserId)
  const byOrder = leaderboardEntries
    .filter((e) => otherIds.includes(e.userId))
    .map((e) => ({ id: e.userId, name: memberNames[e.userId] ?? e.name ?? 'Unknown' }))
  const byOrderIds = new Set(byOrder.map((p) => p.id))
  const rest = otherIds.filter((id) => !byOrderIds.has(id)).map((id) => ({
    id,
    name: memberNames[id] ?? 'Unknown',
  }))
  return [...byOrder, ...rest]
}

export function getDefaultOpponentIdForChallenge(
  acceptedSet: Set<string>,
  currentUserId: string,
  leaderboardEntries: GroupLeaderboardEntry[]
): string | null {
  const other = leaderboardEntries.find(
    (e) => e.userId !== currentUserId && acceptedSet.has(e.userId)
  )
  if (other) return other.userId
  const otherIds = [...acceptedSet].filter((id) => id !== currentUserId)
  return otherIds[0] ?? null
}

export function GroupChallengeHistorySection({
  challenges,
  currentUserId,
  leaderboardEntries,
  memberNames,
  userAvatarId,
  testID,
}: GroupChallengeHistorySectionProps) {
  const theme = useTheme()
  const styles = useMemo(() => getGroupLeaderboardStyles(theme), [theme])

  const [selectedOpponentByChallengeId, setSelectedOpponentByChallengeId] = useState<
    Record<string, string | null>
  >(() => ({}))
  const [visibleCount, setVisibleCount] = useState(GROUP_CHALLENGE_HISTORY_INITIAL_COUNT)

  const acceptedByChallengeId = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const c of challenges) {
      map.set(c.id, getAcceptedParticipantIds(c))
    }
    return map
  }, [challenges])

  useEffect(() => {
    setSelectedOpponentByChallengeId((prev) => {
      const next = { ...prev }
      for (const c of challenges) {
        const accepted = acceptedByChallengeId.get(c.id)
        if (!accepted) continue
        const current = next[c.id]
        const isValid = current && current !== currentUserId && accepted.has(current)
        if (isValid) continue
        next[c.id] = getDefaultOpponentIdForChallenge(accepted, currentUserId, leaderboardEntries)
      }
      return next
    })
  }, [challenges, currentUserId, leaderboardEntries, acceptedByChallengeId])

  const visibleChallenges = useMemo(
    () => challenges.slice(0, visibleCount),
    [challenges, visibleCount]
  )
  const hasMore = challenges.length > visibleCount

  const handleShowMore = useCallback(() => {
    setVisibleCount((n: number) => n + GROUP_CHALLENGE_HISTORY_PAGE_SIZE)
  }, [])

  const handleSelectParticipant = useCallback((challengeId: string, userId: string) => {
    setSelectedOpponentByChallengeId((prev) => ({ ...prev, [challengeId]: userId }))
  }, [])

  if (challenges.length === 0) {
    return (
      <View style={styles.challengeHistorySection} testID={testID ? `${testID}-section` : undefined}>
        <Text style={styles.challengeHistoryTitle}>{GROUP_CHALLENGE_HISTORY_TITLE}</Text>
        <View style={styles.challengeHistoryEmpty}>
          <Text style={styles.challengeHistoryEmptyText}>
            {GROUP_CHALLENGE_HISTORY_EMPTY_MESSAGE}
          </Text>
        </View>
      </View>
    )
  }

  return (
    <View style={styles.challengeHistorySection} testID={testID ? `${testID}-section` : undefined}>
      <Text style={styles.challengeHistoryTitle}>{GROUP_CHALLENGE_HISTORY_TITLE}</Text>
      {visibleChallenges.map((challenge) => {
        const accepted = acceptedByChallengeId.get(challenge.id) ?? new Set<string>()
        const acceptedOtherParticipants = buildAcceptedOtherParticipants(
          accepted,
          currentUserId,
          leaderboardEntries,
          memberNames
        )
        const selectedId = selectedOpponentByChallengeId[challenge.id] ?? null
        const entry = groupChallengeToPickHistoryEntry(challenge, currentUserId, selectedId, {
          logMissingMatch: false,
        })
        const friendName = selectedId ? (memberNames[selectedId] ?? 'Unknown') : ''
        const selectedEntry = selectedId
          ? leaderboardEntries.find((e) => e.userId === selectedId)
          : undefined
        const challengeWithAvatars = challenge as ChallengeWithParticipantAvatars
        const friendAvatarId =
          (selectedId && challengeWithAvatars.participantAvatarIdsById?.[selectedId]) ??
          selectedEntry?.avatarId ??
          undefined
        const friendAvatarConfig =
          (selectedId ? challengeWithAvatars.participantAvatarConfigsById?.[selectedId] : null) ??
          selectedEntry?.avatarConfig ??
          null
        const friendAvatarUrl =
          (selectedId && challengeWithAvatars.participantAvatarsById?.[selectedId]) ||
          selectedEntry?.avatar
        const friendFallbackUrl =
          friendAvatarUrl && String(friendAvatarUrl).startsWith('http') ? friendAvatarUrl : undefined

        const picker =
          acceptedOtherParticipants.length > 0 ? (
            <ChallengeParticipantSwitcher
              participants={acceptedOtherParticipants}
              selectedOpponentId={selectedId}
              onSelectParticipant={(userId) => handleSelectParticipant(challenge.id, userId)}
              testID={testID ? `${testID}-switcher-${challenge.id}` : undefined}
            />
          ) : null

        return (
          <View
            key={challenge.id}
            style={styles.challengeHistoryCardWrap}
            testID={testID ? `${testID}-card-${challenge.id}` : undefined}
          >
            {entry ? (
              <MatchResultCard
                data={entry}
                friendName={friendName}
                userAvatarId={userAvatarId}
                friendAvatarId={friendAvatarId ?? null}
                friendAvatarConfig={friendAvatarConfig}
                friendFallbackUrl={friendFallbackUrl}
                headerContent={picker}
                testID={testID ? `${testID}-match-card-${challenge.id}` : undefined}
              />
            ) : (
              picker
            )}
          </View>
        )
      })}
      {hasMore && (
        <Pressable
          style={({ pressed, hovered }) => [
            styles.challengeHistoryShowMoreButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.challengeHistoryShowMoreButtonPressed,
            hovered && Platform.OS === 'web' && styles.challengeHistoryShowMoreButtonHover,
          ]}
          onPress={handleShowMore}
          accessibilityLabel={GROUP_CHALLENGE_HISTORY_SHOW_MORE}
          accessibilityRole="button"
          testID={testID ? `${testID}-show-more` : undefined}
        >
          <Text style={styles.challengeHistoryShowMoreText}>
            {GROUP_CHALLENGE_HISTORY_SHOW_MORE}
          </Text>
        </Pressable>
      )}
    </View>
  )
}
